import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import RuleRing from "../components/RuleRing";
import CodeBlock from "../components/CodeBlock";
import ThemeToggle from "../components/ThemeToggle";
import LanguageToggle from "../components/LanguageToggle";
import { useReveal } from "../lib/useReveal";
import styles from "./Home.module.css";

const GALLERY_ITEMS = [
  { key: "profile", path: "/docs/kira-arts/cards/profile", accent: "var(--rule-custom)" },
  { key: "member", path: "/docs/kira-arts/cards/welcome-leave", accent: "var(--rule-role)" },
  {
    key: "levelup",
    path: "/docs/kira-arts/cards/levelup",
    accent: "linear-gradient(135deg, var(--rule-nitro-1), var(--rule-nitro-2))",
  },
  { key: "achievement", path: "/docs/kira-arts/cards/achievement", accent: "var(--rule-nitro-2)" },
  {
    key: "ship",
    path: "/docs/kira-arts/cards/ship",
    accent: "linear-gradient(135deg, var(--rule-custom), var(--rule-nitro-1))",
  },
  { key: "leaderboard", path: "/docs/kira-arts/cards/leaderboard", accent: "var(--rule-fallback)" },
  { key: "nowplaying", path: "/docs/kira-arts/cards/now-playing", accent: "var(--rule-nitro-3)" },
  {
    key: "giveaway",
    path: "/docs/kira-arts/cards/giveaway",
    accent: "linear-gradient(135deg, var(--rule-nitro-2), var(--rule-nitro-3))",
  },
  { key: "birthday", path: "/docs/kira-arts/cards/birthday", accent: "var(--rule-nitro-1)" },
  {
    key: "marriage",
    path: "/docs/kira-arts/cards/marriage",
    accent: "linear-gradient(135deg, var(--rule-nitro-3), var(--rule-custom))",
  },
  { key: "weeklyrecap", path: "/docs/kira-arts/cards/weekly-recap", accent: "var(--rule-role)" },
  {
    key: "codesnippet",
    path: "/docs/kira-arts/cards/code-snippet",
    accent: "linear-gradient(135deg, var(--rule-fallback), var(--rule-nitro-1))",
  },
];

const INSTALL_CODE = `npm install kira-arts`;

const USAGE_CODE = `import { profileImage } from "kira-arts";

const buffer = await profileImage(userId, { guildId });`;

export default function Home() {
  const { t } = useTranslation();
  const gallerySection = useReveal<HTMLElement>();
  const installSection = useReveal<HTMLElement>();

  return (
    <div className={styles.page}>
      <header className={styles.nav}>
        <span className={styles.brand}>
          kira<span>-arts</span>
        </span>
        <div className={styles.navLinks}>
          <Link to="/docs/kira-arts/playground">{t("nav.playground")}</Link>
          <Link to="/docs/kira-arts/getting-started">{t("nav.docs")}</Link>
          <a href="https://github.com/worddevs/kira-arts" target="_blank" rel="noreferrer">
            {t("nav.github")}
          </a>
          <ThemeToggle />
          <LanguageToggle />
        </div>
      </header>

      <section className={styles.hero}>
        <div className={styles.heroText}>
          <p className={styles.eyebrow}>{t("home.eyebrow")}</p>
          <h1>
            {t("home.titleLine1")}
            <br />
            <span className={styles.accent}>{t("home.titleAccent")}</span>{" "}
            {t("home.titleEnd")}
          </h1>
          <p className={styles.lede}>
            <Trans i18nKey="home.lede" components={[<code key="0" />]} />
          </p>
          <div className={styles.ctaRow}>
            <Link to="/docs/kira-arts/getting-started" className={styles.ctaPrimary}>
              {t("home.ctaStart")}
            </Link>
            <Link to="/docs/kira-arts/color-system" className={styles.ctaSecondary}>
              {t("home.ctaColorSystem")}
            </Link>
          </div>
        </div>

        <div className={styles.heroDemo}>
          <RuleRing />
        </div>
      </section>

      <section className={styles.section} ref={gallerySection.ref}>
        <p className={styles.sectionEyebrow}>{t("home.gallery.eyebrow")}</p>
        <h2 className={styles.sectionTitle}>{t("home.gallery.title")}</h2>

        <div className={`${styles.gallery} ${gallerySection.visible ? styles.galleryVisible : ""}`}>
          {GALLERY_ITEMS.map((item, i) => (
            <Link
              key={item.key}
              to={item.path}
              className={styles.tile}
              style={{
                ["--accent" as string]: item.accent,
                ["--tile-delay" as string]: `${i * 60}ms`,
              }}
            >
              <span className={styles.tileFn}>{t(`home.gallery.items.${item.key}.fn`)}</span>
              <span className={styles.tileTitle}>
                {t(`home.gallery.items.${item.key}.title`)}
              </span>
              <span className={styles.tileTagline}>
                {t(`home.gallery.items.${item.key}.tagline`)}
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section className={styles.section} ref={installSection.ref}>
        <div className={`${styles.installGrid} ${installSection.visible ? styles.installVisible : ""}`}>
          <div>
            <p className={styles.sectionEyebrow}>{t("home.install.eyebrow")}</p>
            <h2 className={styles.sectionTitle}>{t("home.install.title")}</h2>
            <p className={styles.installCaption}>{t("home.install.caption")}</p>
            <Link to="/docs/kira-arts/getting-started" className={styles.ctaSecondary}>
              {t("home.install.cta")}
            </Link>
          </div>

          <CodeBlock
            tabs={[
              { label: "install", code: INSTALL_CODE, language: "bash" },
              { label: "usage", code: USAGE_CODE },
            ]}
          />
        </div>
      </section>

      <footer className={styles.footer}>
        <div>
          <span className={styles.brand}>
            kira<span>-arts</span>
          </span>
          <p className={styles.footerTagline}>{t("home.footer.tagline")}</p>
        </div>

        <div className={styles.footerLinks}>
          <a href="https://github.com/worddevs/kira-arts" target="_blank" rel="noreferrer">
            GitHub
          </a>
          <a href="https://www.npmjs.com/package/kira-arts" target="_blank" rel="noreferrer">
            npm
          </a>
          <Link to="/docs/kira-arts/getting-started">{t("nav.docs")}</Link>
        </div>

        <p className={styles.footerLegal}>{t("home.footer.legal")}</p>
      </footer>
    </div>
  );
}
